import { useStore } from '@nanostores/react';
import { formatDate } from 'date-fns';
import { useState } from 'react';
import { toast } from 'react-toastify';
import { Button } from '~/components/ui/Button';
import { Switch } from '~/components/ui/Switch';
import { PricingSelector } from '~/components/ui/pricing/Card/PricingSelector';
import { createSubscription } from '~/lib/.client/api/organizations';
import { sendAnalyticsEvent } from '~/lib/analytics.client';
import type { PricingCardDropdownOption } from '~/lib/stores/pricing/types';
import { teamStore } from '~/lib/stores/team';
import { userStore } from '~/lib/stores/user';
import { AnalyticsAction, AnalyticsTrackEvent } from '~/types/analytics';
import { teamsPricingList } from '~/utils/pricing';
import { withSpinner } from '~/utils/withSpinner';

export const OrderSummary = () => {
  const createTeamData = useStore(teamStore.createTeamData);
  const user = useStore(userStore.user);
  const [isLoading, setIsLoading] = useState(false);

  const pricingInfo = teamsPricingList.find((data) => {
    return data.level === createTeamData?.level;
  });

  if (!createTeamData || !user || !pricingInfo) {
    return null;
  }

  const teamPlansOptions: PricingCardDropdownOption[] = teamsPricingList.map((pricing) => ({
    pricing,
    badge: pricingInfo.level === pricing.level ? 'current' : undefined,
  }));

  const isYearlyPrice = createTeamData.interval === 'yearly';
  const basePrice = isYearlyPrice ? pricingInfo?.rateMonthly.year : pricingInfo?.rateMonthly.month;

  const paidMembers = createTeamData.members.filter((member) => !!member.withMembership);

  const additionalSeatsAmount = paidMembers.length ? paidMembers.length - 1 : 0;

  const additionalSeatPrice = additionalSeatsAmount * basePrice;
  const totalPrice = (basePrice + additionalSeatPrice) * (isYearlyPrice ? 12 : 1);

  const handleCheckout = async () => {
    sendAnalyticsEvent({
      action: AnalyticsAction.Track,
      payload: {
        event: AnalyticsTrackEvent.TeamCheckoutStarted,
        properties: {
          organization_id: createTeamData.organization.id,
          total_members: createTeamData.members,
          paid_seats: paidMembers.length,
        },
      },
    });
    withSpinner({
      action: async () => {
        try {
          const response = await createSubscription({
            organization: createTeamData.organization,
            interval: createTeamData.interval,
            level: createTeamData.level,
            invitees: paidMembers.map((member) => member.user.providerId),
          });
          window.open(response?.sessionUrl, '_self');
        } catch {
          toast.error('Failed to proceed to checkout');
        } finally {
          teamStore.createTeamData.set(null);
          teamStore.selectedMembers.set([]);
        }
      },
      onStateUpdate: setIsLoading,
    });
  };

  return (
    <div className="flex flex-col gap-6 py-6 pl-4 pr-8 grow-1 sm:px-8">
      <div className="flex justify-between gap-4 items-baseline">
        <div className="font-bold text-xl text-nowrap">Order Summary</div>
        <div className="flex items-center gap-2 grow-1 justify-end">
          <label htmlFor="interval-switch" className="text-bolt-elements-textSecondary text-sm text-nowrap">
            Annual billing
          </label>
          <Switch
            size="sm"
            id="interval-switch"
            checked={isYearlyPrice}
            onCheckedChange={(isChecked) => {
              teamStore.createTeamData.set({
                ...createTeamData,
                interval: isChecked ? 'yearly' : 'monthly',
              });
            }}
          />
        </div>
      </div>

      <PricingSelector
        options={teamPlansOptions}
        label="Monthly tokens per member"
        selectedPricing={pricingInfo}
        onSelectPricing={(pricing) => {
          teamStore.createTeamData.set({
            ...createTeamData,
            level: pricing.level,
          });
        }}
      />

      <div className="flex justify-between items-start">
        <div className="flex flex-col gap-1">
          <div className="capitalize text-base font-semibold text-bolt-ds-textPrimary">
            {pricingInfo.plan} {pricingInfo.rateMonthly.month} plan
          </div>
          <div className="text-sm text-bolt-ds-textSecondary">
            Base of $
            {isYearlyPrice ? pricingInfo.rateMonthly.year.toFixed(2) : pricingInfo.rateMonthly.month.toFixed(2)} / month
          </div>
        </div>
        <div className="text-lg font-semibold text-bolt-ds-textPrimary">
          ${isYearlyPrice ? pricingInfo.rateMonthly.year : pricingInfo.rateMonthly.month}
        </div>
      </div>

      <div className="flex justify-between items-start">
        <div className="flex flex-col gap-1">
          <div className="text-base font-semibold text-bolt-ds-textPrimary">
            {additionalSeatsAmount > 0 ? <>{additionalSeatsAmount} extra paid seats</> : <>No extra paid seats</>}
          </div>
          <div className="text-sm text-bolt-ds-textSecondary">
            {additionalSeatsAmount} x $
            {isYearlyPrice ? pricingInfo.rateMonthly.year.toFixed(2) : pricingInfo.rateMonthly.month.toFixed(2)} / month
          </div>
        </div>
        <div className="text-lg font-semibold text-bolt-ds-textPrimary">
          ${additionalSeatsAmount * (isYearlyPrice ? pricingInfo.rateMonthly.year : pricingInfo.rateMonthly.month)}
        </div>
      </div>

      <div className="w-full h-px bg-bolt-ds-border-divider" />

      <div className="flex justify-between items-start">
        <div className="flex flex-col gap-1">
          <div className="capitalize text-base font-semibold text-bolt-ds-textPrimary">Total</div>
          <div className="text-sm text-bolt-ds-textSecondary">
            Billed every {isYearlyPrice ? 'year' : 'month'} from {formatDate(new Date(), 'MMMM do')}
          </div>
        </div>
        <div className="text-lg font-semibold text-bolt-ds-textPrimary">${totalPrice.toLocaleString()}</div>
      </div>

      <Button
        onClick={handleCheckout}
        className="gap-2 items-center"
        disabled={isLoading || !paidMembers.length}
        loading={isLoading}
      >
        <span className="text-base">Checkout now</span>
        <span className="i-heroicons:arrow-top-right-on-square text-lg" />
      </Button>
    </div>
  );
};
